import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { EventFormEvent } from 'components/events/EventForm/types'
import type {
  DeskTranslationFill,
  DeskTranslationResult,
  DeskTranslationTarget
} from 'pages/Desk/deskTranslation'
import { pairsToTranslate, toFormFill } from 'pages/Desk/deskTranslation'
import { translate } from 'utils/translate'

// docs/spec-desk-auto-translate.md: the desk translates the filled side of a
// candidate's title, subtitle, description and price into the empty side when
// the candidate opens (D-01), through the existing `translate` Edge Function.

/**
 * D-07: `translate` throws on an invoke error and with a TypeError when the
 * function answers HTTP 200 with `{ error }` and no `translation` (a DeepL
 * quota 456 arrives that way). This wrapper never throws: any failure, and an
 * empty translation, is null. The shared `translate` stays as it is, because
 * every other admin form depends on it.
 */
export const translateOrNull = async (text: string, target: DeskTranslationTarget): Promise<string | null> => {
  try {
    const result = await translate(text, target)
    return typeof result === 'string' && result.trim() !== '' ? result : null
  } catch {
    return null
  }
}

/**
 * The translation cache lives under the 'desk' prefix (the D-05 key), and
 * every desk decision and paste invalidates that prefix. The invalidations
 * skip it with this predicate, so a decision never re-spends DeepL quota.
 */
export const isDeskTranslationQuery = (query: { queryKey: readonly unknown[] }): boolean =>
  query.queryKey[0] === 'desk' && query.queryKey[1] === 'translation'

interface DeskTranslationData {
  fill: DeskTranslationFill
  failed: boolean
}

export interface DeskTranslation {
  fill: DeskTranslationFill | null
  pending: boolean
  failed: boolean
}

/**
 * Translate a candidate's empty language sides once per candidate per session
 * (D-05). The source texts are in the key, so a re-read candidate whose text
 * changed is translated again. The four requests run in parallel and settle
 * independently (D-07): a failed field is left out of `fill` and sets
 * `failed`. A letterless price is copied without a call (D-03). `enabled` is
 * false outside approve mode (D-02).
 */
export const useDeskTranslation = (
  candidateId: number | undefined,
  prefill: EventFormEvent | undefined,
  enabled: boolean
): DeskTranslation => {
  const pairs = useMemo(() => (prefill ? pairsToTranslate(prefill) : []), [prefill])

  const query = useQuery<DeskTranslationData>({
    queryKey: ['desk', 'translation', candidateId, pairs.map((pair) => [pair.field, pair.source])],
    queryFn: async () => {
      const settled = await Promise.allSettled(
        pairs.map((pair) => (pair.copyOnly ? Promise.resolve(pair.source) : translateOrNull(pair.source, pair.target)))
      )
      const results: DeskTranslationResult[] = settled.map((outcome, index) => ({
        field: pairs[index].field,
        value: outcome.status === 'fulfilled' ? outcome.value : null
      }))
      return { fill: toFormFill(results), failed: results.some((result) => result.value === null) }
    },
    enabled: enabled && candidateId !== undefined && pairs.length > 0,
    // Once per session: never stale, never collected, never retried. The
    // refetch flags are explicit because setQueryDefaults(['desk']) turns
    // focus and mount refetches on for the rest of the desk.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  return {
    fill: query.data?.fill ?? null,
    pending: query.fetchStatus === 'fetching',
    failed: query.data?.failed ?? false
  }
}
